'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { 
  differenceInDays, 
  endOfMonth, 
  subMonths, 
  startOfMonth, 
  isWithinInterval, 
  parseISO, 
  format, 
  addMonths 
} from 'date-fns';
import { useTransactions } from './TransactionsContext';
import { Budget, BudgetFilters, BudgetStats, BudgetAnalysis, BudgetInsight } from '@/types/budget';
import { Transaction, Category } from '@/types/transaction';
import { 
  calculateBudgetStats, 
  analyzeSpending, 
  generateBudgetInsights, 
  calculateBudgetProgress as calculateBudgetProgressUtil 
} from '@/lib/budget-utils';
import { analyzeSpendingPattern, predictBudgetSpend } from '@/lib/budget/analytics';

interface BudgetsContextType {
  // State
  budgets: Budget[];
  stats: BudgetStats | null;
  loading: boolean;
  error: string | null;
  filters: BudgetFilters;
  insights: BudgetInsight[];
  analysis: BudgetAnalysis[];
  predictions: Record<string, any>;
  alerts: any[];
  spendingPatterns: Record<string, any>;
  
  // Core methods
  createBudget: (budget: Omit<Budget, 'id' | 'createdAt' | 'updatedAt' | 'spent'>) => Promise<Budget>;
  updateBudget: (id: string, updates: Partial<Budget>) => Promise<Budget>;
  deleteBudget: (id: string) => Promise<void>;
  updateFilters: (filters: Partial<BudgetFilters>) => void;
  resetFilters: () => void;
  analyzeSpending: () => Promise<BudgetAnalysis[]>;
  generateInsights: () => Promise<BudgetInsight[]>;
  getCategoryInsights: (categoryId: string) => BudgetInsight[];
  getBudgetProgress: (budget: Budget) => {
    percentage: number;
    remaining: number;
    daysRemaining: number;
    dailyBudget: number;
    dailySpent: number;
    projectedEndSpend: number;
    isOnTrack: boolean;
  };
  getCategorySpending: (categoryId: string, period?: 'day' | 'week' | 'month' | 'year') => number;
  getSuggestedBudgets: () => Promise<BudgetAnalysis[]>;
  recalculateBudgets: () => Promise<Budget[]>;
  getBudgetByCategory: (categoryId: string) => Budget | undefined;
  
  // Enhanced methods (consolidated from EnhancedBudgetsContext)
  predictBudgetSpend: (categoryId: string, daysAhead?: number) => Promise<any>;
  analyzeSpendingPattern: (categoryId: string) => Promise<any>;
  getAlerts: () => Promise<any[]>;
  dismissAlert: (alertId: string) => void;
  applyBudgetSuggestion: (suggestion: any) => Promise<void>;
}

const BudgetsContext = createContext<BudgetsContextType | undefined>(undefined);

interface BudgetsProviderProps {
  children: React.ReactNode;
  initialBudgets?: Budget[];
  initialTransactions?: Transaction[];
  categories?: Category[];
}

export function BudgetsProvider({ 
  children, 
  initialBudgets = [],
  initialTransactions = [],
  categories = []
}: BudgetsProviderProps) {
  const { transactions: contextTransactions } = useTransactions();
  const transactions = useMemo(() => [...contextTransactions, ...initialTransactions], [contextTransactions]);
  
  const [budgets, setBudgets] = useState<Budget[]>(initialBudgets);
  const [stats, setStats] = useState<BudgetStats | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<BudgetFilters>({});
  const [insights, setInsights] = useState<BudgetInsight[]>([]);
  const [analysis, setAnalysis] = useState<BudgetAnalysis[]>([]);
  // Enhanced state variables
  const [predictions, setPredictions] = useState<Record<string, any>>({});
  const [alerts, setAlerts] = useState<any[]>([]);
  const [spendingPatterns, setSpendingPatterns] = useState<Record<string, any>>({});
  
  const now = useMemo(() => new Date(), []);
  const currentMonthStart = useMemo(() => startOfMonth(now), [now]);
  const currentMonthEnd = useMemo(() => endOfMonth(now), [now]);
  const lastMonthStart = useMemo(() => startOfMonth(subMonths(now, 1)), [now]);
  const lastMonthEnd = useMemo(() => endOfMonth(subMonths(now, 1)), [now]);

  // Define analyzeSpending and generateBudgetInsights functions first
  const analyzeSpendingForCategory = useCallback((transactions: Transaction[]): BudgetAnalysis => {
    const totalSpent = transactions.reduce((sum, tx) => sum + tx.amount, 0);
    const avgSpent = transactions.length > 0 ? totalSpent / Math.max(1, transactions.length) : 0;
    const now = new Date();
    const categoryId = transactions[0]?.category?.id || '';
    
    // Calculate confidence based on transaction count
    const confidence = transactions.length > 10 ? 'high' : transactions.length > 5 ? 'medium' : 'low' as const;
    
    // Generate insights based on spending patterns
    const insights: string[] = [];
    if (totalSpent > 0) {
      insights.push(`Spent $${totalSpent.toFixed(2)} in this category`);
      insights.push(`Average monthly spend: $${avgSpent.toFixed(2)}`);
    }
    
    return {
      categoryId,
      currentSpend: totalSpent,
      averageSpend: avgSpent,
      suggestedBudget: Math.ceil(avgSpent * 1.1), // 10% buffer
      confidence,
      insights
    };
  }, []);

  const generateBudgetInsightsForBudget = useCallback((budget: Budget, transactions: Transaction[]): BudgetInsight[] => {
    const categoryTransactions = transactions.filter(tx => tx.category?.id === budget.category.id);
    const monthlySpend = categoryTransactions.reduce((sum, tx) => sum + tx.amount, 0);
    const isOverspent = monthlySpend > budget.amount;
    const now = new Date();
    
    // Calculate days remaining in the budget period
    const endDate = budget.endDate ? new Date(budget.endDate) : endOfMonth(now);
    const daysRemaining = differenceInDays(endDate, now);
    
    // Generate appropriate message based on budget status
    let message: string;
    let type: 'info' | 'warning' | 'alert' | 'suggestion' = 'info';
    
    if (isOverspent) {
      message = `You've spent $${(monthlySpend - budget.amount).toFixed(2)} over budget this month`;
      type = 'alert';
    } else if (monthlySpend / budget.amount > 0.8) {
      message = `You've used ${Math.round((monthlySpend / budget.amount) * 100)}% of your ${budget.category.name} budget`;
      type = 'warning';
    } else if (daysRemaining < 7) {
      message = `${daysRemaining} days left in your ${budget.period} budget period`;
      type = 'suggestion';
    } else {
      message = `You have $${(budget.amount - monthlySpend).toFixed(2)} remaining in your ${budget.category.name} budget`;
    }
    
    return [{
      id: `insight-${budget.id}-${now.getTime()}`,
      budgetId: budget.id,
      type,
      message,
      createdAt: now.toISOString(),
      metadata: {
        amount: Math.abs(monthlySpend - budget.amount),
        isOverspent,
        monthlySpend,
        budgetAmount: budget.amount,
        daysRemaining
      }
    }];
  }, []);
  
  // Now define the main functions that use the above helpers
  const analyzeSpending = useCallback(async (): Promise<BudgetAnalysis[]> => {
    const analysisResults = budgets.map(budget => {
      const categoryTransactions = transactions.filter(tx => tx.category?.id === budget.category.id);
      return analyzeSpendingForCategory(categoryTransactions);
    });
    
    setAnalysis(analysisResults);
    return analysisResults;
  }, [budgets, transactions, analyzeSpendingForCategory]);

  const generateBudgetInsights = useCallback(async (): Promise<BudgetInsight[]> => {
    const allInsights = budgets.flatMap(budget => 
      generateBudgetInsightsForBudget(budget, transactions)
    );
    
    setInsights(allInsights);
    return allInsights;
  }, [budgets, transactions, generateBudgetInsightsForBudget]);

    // Calculate spending for a category in a given period
    const getCategorySpending = useCallback((categoryId: string, period: 'day' | 'week' | 'month' | 'year' | 'weekly' | 'bi-weekly' | 'monthly' | 'yearly' = 'monthly'): number => {
      let startDate = new Date();
      
      // Normalize the period to the format used in the function
      const normalizedPeriod = period.replace(/-/g, '').toLowerCase();
      
      switch (normalizedPeriod) {
        case 'day':
          startDate.setDate(now.getDate() - 1);
          break;
        case 'week':
        case 'weekly':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'biweekly':
          startDate.setDate(now.getDate() - 14);
          break;
        case 'year':
        case 'yearly':
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        case 'month':
        case 'monthly':
        default:
          startDate.setMonth(now.getMonth() - 1);
          break;
      }
      
      return transactions
        .filter(tx => 
          tx.category?.id === categoryId && 
          new Date(tx.date) >= startDate &&
          tx.amount < 0 // Only count expenses
        )
        .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
    }, [transactions, now]);

  // Load budgets from API
  const loadBudgets = useCallback(async () => {
    try {
      setLoading(true);
      // In a real app, this would fetch from an API
      // For now, we'll use the initialBudgets prop
      const budgetsWithSpent = initialBudgets.map(budget => ({
        ...budget,
        spent: getCategorySpending(budget.category.id, budget.period as any)
      }));
      
      // Only update if budgets have actually changed
      if (JSON.stringify(budgetsWithSpent) !== JSON.stringify(budgets)) {
        setBudgets(budgetsWithSpent);
      }
      
      // Calculate initial stats with all required properties
      const baseStats = calculateBudgetStats(budgetsWithSpent, transactions);
      const initialStats: BudgetStats = {
        ...baseStats,
        daysRemaining: 30 - now.getDate(),
        averageDailySpend: baseStats.totalSpent / now.getDate(),
        projectedEndSpend: (baseStats.totalSpent / now.getDate()) * 30,
        projectedOverspend: null,
        categories: budgetsWithSpent.map(b => ({
          id: b.category.id,
          name: b.category.name,
          budgeted: b.amount,
          spent: b.spent,
          remaining: b.amount - b.spent,
          percentage: b.amount > 0 ? (b.spent / b.amount) * 100 : 0
        }))
      };
      
      // Only update if stats have actually changed
      if (JSON.stringify(initialStats) !== JSON.stringify(stats)) {
        setStats(initialStats);
      }
      
      // Generate initial analysis and insights
      const [newAnalysis, newInsights] = await Promise.all([
        analyzeSpending(),
        generateBudgetInsights()
      ]);
      
      // Update state only if data has changed
      if (JSON.stringify(newAnalysis) !== JSON.stringify(analysis)) {
        setAnalysis(newAnalysis);
      }
      if (JSON.stringify(newInsights) !== JSON.stringify(insights)) {
        setInsights(newInsights);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load budgets');
      setError(error.message);
      console.error('Error loading budgets:', error);
    } finally {
      setLoading(false);
    }
  }, [initialBudgets, transactions, getCategorySpending, now, analyzeSpending, generateBudgetInsights]);

  // Analyze spending patterns
  const performSpendingAnalysis = useCallback(async (): Promise<BudgetAnalysis[]> => {
    // Create an array of analysis promises
    const analysisPromises = budgets.map(async (budget) => {
      const categoryId = budget.category.id;
      const period = budget.period;
      
      // Calculate spending for the category and period
      const spent = getCategorySpending(categoryId, period);
      
      // Calculate average spending (simplified for example)
      const averageSpend = spent / 3; // Assuming 3 months of data
      
      // Create analysis result
      return {
        categoryId,
        currentSpend: spent,
        averageSpend,
        suggestedBudget: Math.ceil(averageSpend * 1.1), // 10% buffer
        confidence: 'medium' as const,
        insights: [
          `Your current spending is $${spent.toFixed(2)} this period`,
          `Suggested budget: $${Math.ceil(averageSpend * 1.1).toFixed(2)}`
        ]
      };
    });
    
    try {
      const results = await Promise.all(analysisPromises);
      setAnalysis(results);
      return results;
    } catch (error) {
      console.error('Error performing spending analysis:', error);
      return [];
    }
  }, [budgets, transactions, getCategorySpending]);

  // Generate insights
  const generateInsights = useCallback(async (): Promise<BudgetInsight[]> => {
    const allInsights: BudgetInsight[] = [];
    
    for (const budget of budgets) {
      const budgetInsights = generateBudgetInsightsForBudget(budget, transactions);
      allInsights.push(...budgetInsights);
    }
    
    setInsights(allInsights);
    return allInsights;
  }, [budgets, transactions]);
  
  // Calculate budget progress for a specific budget
  const getBudgetProgress = useCallback((budget: Budget) => {
    const spent = getCategorySpending(budget.category.id, budget.period as any);
    const percentage = Math.min(100, (spent / budget.amount) * 100);
    const remaining = budget.amount - spent;
    const now = new Date();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const daysRemaining = daysInMonth - now.getDate();
    const dailyBudget = budget.amount / daysInMonth;
    const dailySpent = budget.spent / (daysInMonth - daysRemaining);
    const projectedEndSpend = dailySpent * daysInMonth;
    
    return {
      percentage,
      remaining,
      daysRemaining,
      dailyBudget,
      dailySpent,
      projectedEndSpend,
      isOnTrack: projectedEndSpend <= budget.amount
    };
  }, [getCategorySpending]);
  
  // Get suggested budgets based on spending patterns
  const getSuggestedBudgets = useCallback(async (): Promise<BudgetAnalysis[]> => {
    const analysis = await performSpendingAnalysis();
    return analysis;
  }, [performSpendingAnalysis]);
  
  // Recalculate all budgets based on current transactions
  const recalculateBudgets = useCallback(async (): Promise<Budget[]> => {
    const updatedBudgets = budgets.map(budget => ({
      ...budget,
      spent: getCategorySpending(budget.category.id, budget.period as any)
    }));
    
    setBudgets(updatedBudgets);
    setStats(calculateBudgetStats(updatedBudgets, transactions));
    await generateInsights();
    
    return updatedBudgets;
  }, [budgets, transactions, generateInsights, getCategorySpending]);
  
  // Get budget by category ID
  const getBudgetByCategory = useCallback((categoryId: string): Budget | undefined => {
    return budgets.find(b => b.category.id === categoryId);
  }, [budgets]);

  // Calculate budget statistics with all required properties
  const calculateStats = useCallback((): BudgetStats => {
    const baseStats = calculateBudgetStats(budgets, transactions);
    return {
      ...baseStats,
      daysRemaining: 30, // Calculate based on current date
      averageDailySpend: baseStats.totalSpent / new Date().getDate(),
      projectedEndSpend: (baseStats.totalSpent / new Date().getDate()) * 30,
      projectedOverspend: null,
      categories: budgets.map(b => ({
        id: b.category.id,
        name: b.category.name,
        budgeted: b.amount,
        spent: b.spent,
        remaining: b.amount - b.spent,
        percentage: b.amount > 0 ? (b.spent / b.amount) * 100 : 0
      }))
    };
  }, [budgets]);

  // Create a new budget
  const createBudget = async (budgetData: Omit<Budget, 'id' | 'createdAt' | 'updatedAt' | 'spent'>): Promise<Budget> => {
    setLoading(true);
    setError(null);
    
    try {
      // Check for existing budget for this category and period
      const exists = budgets.some(
        b => b.category.id === budgetData.category.id && b.period === budgetData.period
      );
      
      if (exists) {
        throw new Error(`A budget already exists for ${budgetData.category.name} in this period`);
      }
      
      const spent = getCategorySpending(budgetData.category.id, budgetData.period as any);
      
      const newBudget: Budget = {
        ...budgetData,
        id: `budget-${Date.now()}`,
        spent,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      const updatedBudgets = [...budgets, newBudget];
      setBudgets(updatedBudgets);
      
      // Update stats and insights
      setStats(calculateBudgetStats(updatedBudgets, transactions));
      await generateInsights();
      
      return newBudget;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create budget');
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update an existing budget
  const updateBudget = async (id: string, updates: Partial<Budget>): Promise<Budget> => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedBudgets = budgets.map(budget => {
        if (budget.id === id) {
          const updatedBudget = { 
            ...budget, 
            ...updates, 
            updatedAt: new Date().toISOString() 
          };
          // Recalculate spent amount if category changed
          if (updates.category) {
            updatedBudget.spent = getCategorySpending(updates.category.id, budget.period);
          }
          return updatedBudget;
        }
        return budget;
      });
      
      setBudgets(updatedBudgets);
      setStats(calculateBudgetStats(updatedBudgets, transactions));
      await generateInsights();
      
      const updatedBudget = updatedBudgets.find(b => b.id === id);
      if (!updatedBudget) throw new Error('Budget not found after update');
      
      return updatedBudget;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update budget');
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete a budget
  const deleteBudget = async (id: string): Promise<void> => {
    // TODO: Implement actual API call
    setBudgets(prev => prev.filter(budget => budget.id !== id));
  };

  // Update filters
  const updateFilters = (newFilters: Partial<BudgetFilters>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
    }));
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({});
  };

  // Enhanced methods implementation (consolidated from EnhancedBudgetsContext)
  const predictBudgetSpendImpl = useCallback(async (categoryId: string, daysAhead: number = 30) => {
    try {
      const prediction = await predictBudgetSpend(transactions, categoryId, daysAhead);
      setPredictions(prev => ({
        ...prev,
        [categoryId]: prediction
      }));
      return prediction;
    } catch (err) {
      console.error('Error predicting budget spend:', err);
      throw err;
    }
  }, [transactions]);
  
  const analyzeSpendingPatternImpl = useCallback(async (categoryId: string) => {
    try {
      const pattern = analyzeSpendingPattern(transactions, categoryId);
      setSpendingPatterns(prev => ({
        ...prev,
        [categoryId]: pattern
      }));
      return pattern;
    } catch (err) {
      console.error('Error analyzing spending pattern:', err);
      throw err;
    }
  }, [transactions]);
  
  const getAlerts = useCallback(async (): Promise<any[]> => {
    return alerts;
  }, [alerts]);
  
  const dismissAlert = useCallback((alertId: string) => {
    setAlerts(prevAlerts => 
      prevAlerts.map(alert => 
        alert.id === alertId ? { ...alert, read: true } : alert
      )
    );
  }, []);
  
  const applyBudgetSuggestion = useCallback(async (suggestion: any) => {
    console.log('Applying budget suggestion:', suggestion);
    // Implementation would update the budget based on the suggestion
  }, []);

  // Get insights for a specific category
  const getCategoryInsights = (categoryId: string): BudgetInsight[] => {
    const budget = budgets.find(b => b.category.id === categoryId);
    if (!budget) return [];
    
    return insights.filter(insight => insight.budgetId === budget.id);
  };

  // Initial load - only run once on mount
  useEffect(() => {
    loadBudgets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // Empty dependency array means this runs once on mount
  
  // Update stats when budgets or transactions change
  useEffect(() => {
    if (budgets.length > 0 || transactions.length > 0) {
      setStats(calculateBudgetStats(budgets, transactions));
    }
  }, [budgets, transactions]);

  const value = useMemo(() => ({
    // State
    budgets,
    stats,
    loading,
    error,
    filters,
    insights,
    analysis,
    predictions,
    alerts,
    spendingPatterns,
    
    // Core methods
    createBudget,
    updateBudget,
    deleteBudget,
    updateFilters,
    resetFilters,
    analyzeSpending: performSpendingAnalysis,
    generateInsights,
    getCategoryInsights,
    getBudgetProgress,
    getCategorySpending,
    getSuggestedBudgets,
    recalculateBudgets,
    getBudgetByCategory,
    
    // Enhanced methods (consolidated from EnhancedBudgetsContext)
    predictBudgetSpend: predictBudgetSpendImpl,
    analyzeSpendingPattern: analyzeSpendingPatternImpl,
    getAlerts,
    dismissAlert,
    applyBudgetSuggestion,
  }), [
    budgets,
    stats,
    loading,
    error,
    filters,
    insights,
    analysis,
    predictions,
    alerts,
    spendingPatterns,
    createBudget,
    updateBudget,
    deleteBudget,
    updateFilters,
    resetFilters,
    performSpendingAnalysis,
    generateInsights,
    getCategoryInsights,
    getBudgetProgress,
    getCategorySpending,
    getSuggestedBudgets,
    recalculateBudgets,
    getBudgetByCategory,
    predictBudgetSpendImpl,
    analyzeSpendingPatternImpl,
    getAlerts,
    dismissAlert,
    applyBudgetSuggestion,
  ]);

  return (
    <BudgetsContext.Provider value={value}>
      {children}
    </BudgetsContext.Provider>
  );
}

export function useBudgets() {
  const context = useContext(BudgetsContext);
  if (context === undefined) {
    throw new Error('useBudgets must be used within a BudgetsProvider');
  }
  return context;
}
